<template>
  <Wizard #default="{getValue,getOptionSet,getOptionSetValue}">
    <WizardStep name="intro">
      <Start title="Los geht's!">
        <p>
          Dies ist der erste Schritt zu Deiner Stelle beim REWE Lieferservice.
          Folgendes solltest Du für Deine Bewerbung bereithalten:
        </p>
        <ul class="mb-4">
          <li>Führerscheindatum</li>
          <li>E-Mail-Adresse</li>
        </ul>
        <p>
          Wir freuen uns auf Deine Bewerbung!
        </p>
      </Start>
    </WizardStep>
    <PersonalTemplate />
    <AvailabilityTemplate />
    <DateOfBirthTemplate />
    <AddressTemplate />
    <WizardStep name="experience">
      <FieldGroup
        :title="`${getValue(
          'personal.firstname'
        )}, wie viel Erfahrung hast Du in dem Bereich, für den Du Dich gerade bewirbst?`"
      >
        <div class="row">
          <div class="col-md-6">
            <TextField
              name="yearsOfExperience"
              placeholder="Erfahrung in Jahren"
              type="number"
            />
          </div>
        </div>
      </FieldGroup>
      <FieldGroup
        flow
        title="Was ist Deine aktuelle bzw. war Deine letzte Tätigkeit?"
      >
        <div class="row">
          <div class="col-md-10">
            <TextField
              name="previousOrCurrentJob"
              placeholder="Aktuelle oder letze Tätigkeit"
            />
          </div>
        </div>
      </FieldGroup>
      <Panel
        slot="footer"
        title="Muss ich vorher schonmal in einem ähnlichen Job gearbeitet haben, um mich zu bewerben?"
      >
        Nein. Es ist natürlich ein Vorteil, wenn Du schon Erfahrung mitbringst,
        aber es ist kein Muss.
      </Panel>
    </WizardStep>
    <WizardStep name="workOnWeekends">
      <FieldGroup
        title="Du wirst in Wechselschichten arbeiten und auch regelmäßig samstags."
      >
        <CheckField
          name="checkboxAccept"
          label="Ja, das passt mir gut"
          as="checkbox"
        />
      </FieldGroup>
      <Panel slot="footer" title="Muss ich auch am Wochenende arbeiten?">
        Ja. Unsere Arbeitstage sind Montag bis Samstag, da solltest Du arbeiten
        können. Du arbeitest in der Regel fünf Tage die Woche, welcher der sechs
        Werktage dann frei ist, wechselt.
      </Panel>
    </WizardStep>
    <WizardStep name="driversLicence">
      <FieldGroup
        title="Seit wann hast Du Deinen Führerschein? (mindestens ein Jahr)"
      >
        <div class="row">
          <div class="col-md-6">
            <TextField
              name="date"
              placeholder="MM.JJJJ"
              label="Monat und Jahr"
              validation="bail|required|matches:/^\d{2}\.\d{4}$/"
            />
          </div>
        </div>
      </FieldGroup>
      <FieldGroup
        flow
        title="Was für Führerscheinklassen hast Du? (mindestens Klasse B)"
      >
        <div class="row">
          <div class="col-md-6">
            <SelectField
              placeholder="Bitte auswählen"
              name="licenceTypes"
              :options="getOptionSet('driversLicences')"
              multiple
              inline-dropdown
            />
          </div>
        </div>
      </FieldGroup>
      <Panel
        slot="footer"
        title="Warum muss ich als Fahrer meinen Führerschein schon mindestens ein Jahr lang haben?"
      >
        Weil wir möchten, dass Du genügend Fahrpraxis hast, bevor Du bei uns
        anfängst.
      </Panel>
    </WizardStep>
    <WizardStep name="languageSkills">
      <FieldGroup title="Wie gut sind Deine Deutschkenntnisse?">
        <OptionsField
          name="level"
          :options="getOptionSet('languageSkills')"
          full-width
        />
      </FieldGroup>
      <Panel slot="footer" title="Wie gut muss mein Deutsch sein?">
        Unsere Lieferfahrer sollten zumindest eine längere Unterhaltung in gutem
        Deutsch führen können, weil sie intensiven Kontakt mit dem Kunden haben.
        Wenn Du Dich als Warenverräumer oder Kommissionierer bewerben möchtest,
        muss Dein Deutsch nicht ganz so gut sein.
      </Panel>
    </WizardStep>
    <WizardStep name="otherJobs">
      <FieldGroup
        title="Interessierst Du Dich für weitere Jobs an diesem Standort?"
        subtitle="Kommt vielleicht auch eine andere Stelle für Dich infrage? Dann weiß unser Recruiter gleich Bescheid."
      >
        <OptionsField
          name="job"
          :options="getOptionSet('otherJobs')"
          multiple
          validation="optional"
          as="checkbox"
        />
      </FieldGroup>
    </WizardStep>
    <PersonalSummaryTemplate>
      <SummaryGroup>
        <SummaryRow
          label="Erfahrung in Jahren"
          :value="getValue('experience.yearsOfExperience')"
        />
        <SummaryRow
          label="Aktuelle oder letzte Tätigkeit"
          :value="getValue('experience.previousOrCurrentJob', '-')"
        />
        <SummaryRow
          label="Wechselschichten"
          :value="getValue('workOnWeekends.checkboxAccept') ? 'ja' : 'nein'"
          checked
        />
        <SummaryRow
          label="Führerschein seit"
          :value="`${getValue(
            'driversLicence.date'
          )}, für die Klasse(n) ${getOptionSetValue(
            'driversLicences',
            'driversLicence.licenceTypes',
            []
          ).join(', ')}`"
          checked
        />
        <SummaryRow
          label="Deutschkenntnisse"
          :value="getOptionSetValue('languageSkills', 'languageSkills.level')"
        />
        <SummaryRow
          label="Weitere Jobinteressen"
          :value="
            getOptionSetValue('otherJobs', 'otherJobs.job', []).join(', ')
          "
          :checked="getValue('otherJobs.job', []).length > 0"
        />
      </SummaryGroup>
    </PersonalSummaryTemplate>
    <WizardStep name="confirmation" confirmation>
      <Confirmation :email="getValue('personal.email')" />
    </WizardStep>
  </Wizard>
</template>
<script>
import AddressTemplate from '../../../clientside/wizard/templates/AddressTemplate.vue';
import AvailabilityTemplate from '../../../clientside/wizard/templates/AvailabilityTemplate.vue';
import DateOfBirthTemplate from '../../../clientside/wizard/templates/DateOfBirthTemplate.vue';
import PersonalSummaryTemplate from '../../../clientside/wizard/templates/PersonalSummaryTemplate.vue';
import PersonalTemplate from '../../../clientside/wizard/templates/PersonalTemplate.vue';

export default {
  components: {
    PersonalTemplate,
    PersonalSummaryTemplate,
    AvailabilityTemplate,
    DateOfBirthTemplate,
    AddressTemplate,
  },
};
</script>
<style lang="scss" scoped>
@use '~tokens/sass/utils';

.confirmation {
  width: 100%;
  margin: 0 auto;
  padding-bottom: utils.f-rem-calc(utils.$v-wizard-sm-gap-y);
  text-align: center;

  @include utils.bs-media-breakpoint-up(md) {
    padding: utils.f-rem-calc(40) utils.f-rem-calc(100) 0;
  }

  @include utils.bs-media-breakpoint-up(lg) {
    padding: utils.f-rem-calc(0) utils.f-rem-calc(100);
  }

  @at-root #{&}__section {
    margin-bottom: utils.f-rem-calc(50);

    @at-root #{&}--has-image {
      display: flex;
    }
  }

  @at-root #{&}__content {
    > * {
      margin-bottom: 0;

      + * {
        margin-top: 10px;
      }
    }

    h2 {
      font-size: 28px;
      margin: 0;
    }

    p {
      line-height: 1.5;
    }
  }

  @at-root #{&}__image {
    margin-right: 16px;
    flex-shrink: 0;
  }

  @at-root #{&}__cta {
    display: flex;
    justify-content: center;
  }
}
</style>
